import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { expValEquals } from '@confluence/feature-experiments';

import CommentIcon from '@atlaskit/icon/core/comment';
import type { AnnotationId } from '@atlaskit/adf-schema/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import type {
	MediaViewerExtensions,
	MediaViewerExtensionsActions,
} from '@atlaskit/media-viewer/types';
import type { Identifier } from '@atlaskit/media-client/identifier';
import type { MediaNodeContext } from '@atlaskit/renderer/actions';

import {
	handleKeyboardShortcutNavigation,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import {
	useIsObjectSidebarShown,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';

export type MediaTargetResolution =
	| { status: 'resolved'; target: MediaNodeContext }
	| { status: 'missing' | 'ambiguous' | 'unsupported' };

const isFileIdentifier = (identifier?: Identifier): identifier is Identifier & { id: string } =>
	!!identifier && 'id' in identifier && typeof identifier.id === 'string';

const i18n = defineMessages({
	viewCommentsLabel: {
		id: 'editor-features.media-viewer.view-comments.label',
		defaultMessage: 'View comments',
		description:
			'Header action label in the media viewer for navigating to a comment attached to the current media item.',
	},
	commentUnavailableTitle: {
		id: 'editor-features.media-viewer.comment-unavailable.title',
		defaultMessage: "This comment isn't available",
		description:
			'Warning flag title shown when a media viewer comment button points to a comment that no longer exists.',
	},
	commentUnavailableDescription: {
		id: 'editor-features.media-viewer.comment-unavailable.description',
		defaultMessage: 'It may have been resolved or deleted.',
		description:
			'Warning flag description shown when a media viewer comment button points to a comment that no longer exists.',
	},
});

const getIdentifierOccurrenceKey = (identifier: Identifier): string | undefined =>
	'occurrenceKey' in identifier && typeof identifier.occurrenceKey === 'string'
		? identifier.occurrenceKey
		: undefined;

type MediaPreviewCommentsAnalyticsSource = 'viewPageScreen' | 'editPageScreen' | 'livePage';

const MEDIA_VIEWER_POPUP_SELECTOR = '[data-testid="media-viewer-popup"]';

// This function exists because navigating in between previewed elements only uses the file id.
// We need a way to map that back to the annotation ID used by comments, however keeping track of
// this at a global level is likely too heavy relative to usage. Better to just compute on the fly.
function findAnnotationForMediaFile(
	annotationIds: AnnotationId[],
	fileId: string,
	isViewPage: boolean,
): AnnotationId | undefined {
	const activeSet = new Set<string>(annotationIds);

	// Locate the media element directly by file ID.
	// - Renderer nodes use: <div data-node-type="media" data-id="<fileId>">
	// - Editor nodes use:   <div data-prosemirror-node-name="media" id="<fileId>">
	const mediaElement = isViewPage
		? // eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document.querySelector(`[data-node-type="media"][data-id="${fileId}"]`)
		: // eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document.getElementById(fileId);

	if (!mediaElement) {
		return undefined;
	}

	// Walk up the DOM to find the enclosing annotation element, but stop once we
	// leave the relevant content root for the current mode.
	let current: Element | null = mediaElement.parentElement;
	while (current) {
		if (current.getAttribute('data-mark-annotation-type') === 'inlineComment') {
			const annotationId = current.id as AnnotationId;

			// For view pages the mark must also be in the 'active' state.
			if (isViewPage && current.getAttribute('data-mark-annotation-state') !== 'active') {
				return undefined;
			}

			return activeSet.has(annotationId) ? annotationId : undefined;
		}

		// Once we reach the top of the renderer / editor container, it's safe to assume this element is not within an annotation
		if (
			(isViewPage && current.classList.contains('ak-renderer-wrapper')) ||
			(!isViewPage && current.classList.contains('ProseMirror'))
		) {
			return undefined;
		}

		current = current.parentElement;
	}

	return undefined;
}

// Mirrors ViewCommentShortcutListener#openInlineComment:
// 1. Emits ON_ANNOTATION_CLICK to select the annotation in the renderer - this transparently also changes comments panel filters if open
// 2. Calls handleKeyboardShortcutNavigation which scrolls renderer + panel
export function handleViewModeAnnotationClick(
	annotationId: AnnotationId,
	isCommentsPanelOpen: boolean = false,
): void {
	getRendererAnnotationEventEmitter().emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
		annotationIds: [annotationId],
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		eventTarget: document.getElementById(annotationId),
	});
	handleKeyboardShortcutNavigation({
		scrollBehavior: ScrollBehavior.SMOOTH,
		block: 'center',
		annotationId,
		commentType: 'INLINE',
		isCommentsPanelOpen,
		context: NavigationContext.RENDERER,
	});
}

// Mirrors EditorCommentShortcutListener#onNavigationClick:
// 1. Emits setselectedannotation to select the annotation in the editor - this transparently also changes comments panel filters if open
// 2. Calls handleKeyboardShortcutNavigation which scrolls editor + panel
export function handleEditModeAnnotationClick(
	annotationId: AnnotationId,
	isCommentsPanelOpen: boolean = false,
): void {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const markElement = document.getElementById(annotationId);
	if (markElement) {
		getEditorAnnotationEventEmitter().emit('setselectedannotation', annotationId);
		handleKeyboardShortcutNavigation({
			scrollBehavior: ScrollBehavior.SMOOTH,
			block: 'center',
			annotationId,
			commentType: 'INLINE',
			isCommentsPanelOpen,
			context: NavigationContext.EDITOR,
		});
	}
}

// Used to handle cases where another user removed this comment
function showUnavailableCommentFlag(
	flags: FlagsStateContainer | undefined,
	title: string,
	description: string,
) {
	void flags?.showWarningFlag({
		id: 'media-viewer-comment-unavailable',
		title,
		description,
	});
}

const MEDIA_VIEWER_UNMOUNT_TIMEOUT_MS = 2000;

/**
 * Waits for the media viewer popup to be removed from the DOM before invoking `callback`.
 * If the popup is already gone (e.g. it unmounted synchronously), the callback fires on
 * the next animation frame. A safety timeout ensures the callback always fires even if
 * the DOM mutation is never observed.
 */
function waitForMediaViewerUnmount(callback: () => void): void {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const popup = document.querySelector(MEDIA_VIEWER_POPUP_SELECTOR);

	if (!popup) {
		// Already unmounted — fire after a rAF to let React finish any pending work.
		requestAnimationFrame(callback);
		return;
	}

	const observer = new MutationObserver(() => {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		if (!document.querySelector(MEDIA_VIEWER_POPUP_SELECTOR)) {
			observer.disconnect();
			clearTimeout(safetyTimer);
			requestAnimationFrame(callback);
		}
	});

	// The media viewer is rendered inside an Atlaskit portal (atlaskit-portal-container),
	// so we observe document.body with subtree:true to catch the removal regardless of
	// how deep in the portal tree the popup lives.
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Safety net: if the observer never fires (e.g. the popup is removed in an
	// unexpected way), ensure we still invoke the callback.
	const safetyTimer = window.setTimeout(() => {
		observer.disconnect();
		callback();
	}, MEDIA_VIEWER_UNMOUNT_TIMEOUT_MS);
}

// Resolves the media viewer's selected identifier to the media node it is showing, so the
// Media Viewer comment sidebar can read that node's annotations. Only the view-page branch is
// implemented; editor/live resolution uses the media plugin's mounted nodes instead and arrives
// with the editor/live change.
export function useResolveMediaTarget({
	isViewPage,
}: {
	isViewPage: boolean;
}): (identifier?: Identifier) => MediaTargetResolution {
	const { rendererActions } = useRendererActions();

	const resolveMediaTarget = useCallback(
		(identifier?: Identifier) => {
			if (!isFileIdentifier(identifier)) {
				return { status: 'unsupported' as const };
			}
			if (!isViewPage) {
				return { status: 'unsupported' as const };
			}

			const result = rendererActions?.getMediaNodeContext({
				id: identifier.id,
				occurrenceKey: getIdentifierOccurrenceKey(identifier),
			});
			if (!result || result.status !== 'resolved') {
				return result ?? { status: 'missing' as const };
			}
			return { status: 'resolved' as const, target: result.context };
		},
		[isViewPage, rendererActions],
	);

	return resolveMediaTarget;
}

// Builds the media viewer header action used to navigate from a media item to its comment thread.
export function useMediaCommentExtensions({
	isViewPage,
	analyticsSource,
	flags,
}: {
	isViewPage: boolean;
	analyticsSource: MediaPreviewCommentsAnalyticsSource;
	flags?: FlagsStateContainer;
}): MediaViewerExtensions | undefined {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getCurrentCommentsPanelState } = useCommentsDataActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const isCommentsPanelShown = isObjectSidebarShown && panel?.id === PanelName.CommentsPanel;
	return useMemo((): MediaViewerExtensions | undefined => {
		if (!expValEquals('cc_comments_media_preview_integration', 'isEnabled', true)) {
			return undefined;
		}

		const getActiveAnnotationIds = (): AnnotationId[] =>
			getCurrentCommentsPanelState().orderedActiveAnnotationIdList?.map(
				({ threadKey }: { threadKey: string }) => threadKey,
			) ?? [];

		return {
			headerActions: [
				{
					label: intl.formatMessage(i18n.viewCommentsLabel),
					icon: <CommentIcon label="" spacing="spacious" />,
					isVisible: (identifier: Identifier) => {
						// Only show icon when image has comments
						// NOTE - this is called whenever the header section is rendered (can be multiple times per image), so be careful with non-performant updates!

						// Media viewer identifiers can also be external-image items, which do not have an `id`.
						if (!('id' in identifier)) {
							return false;
						}

						const annotationId = findAnnotationForMediaFile(
							getActiveAnnotationIds(),
							identifier.id,
							isViewPage,
						);

						return !!annotationId;
					},
					onClick: (identifier: Identifier, actions: MediaViewerExtensionsActions) => {
						// Media viewer identifiers can also be external-image items, which do not have an `id`.
						if (!('id' in identifier)) {
							return;
						}

						// Re-check on click in case the comment was deleted or resolved after the button rendered.
						const annotationId = findAnnotationForMediaFile(
							getActiveAnnotationIds(),
							identifier.id,
							isViewPage,
						);

						// Fire regardless of outcome, so we can get proper click data
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'mediaPreviewCommentsButton',
								source: analyticsSource,
								attributes: {
									context: 'mediaViewer',
									fileId: identifier.id,
									annotationId: annotationId ?? null,
								},
							},
						}).fire();

						if (!annotationId) {
							showUnavailableCommentFlag(
								flags,
								intl.formatMessage(i18n.commentUnavailableTitle),
								intl.formatMessage(i18n.commentUnavailableDescription),
							);
							return;
						}

						actions.close();

						// Defer navigation until after the viewer close has started so selection and scrolling
						// happen against the underlying page, not the still-open modal.
						if (isViewPage) {
							requestAnimationFrame(() => {
								handleViewModeAnnotationClick(annotationId, isCommentsPanelShown);
							});
						} else {
							waitForMediaViewerUnmount(() => {
								handleEditModeAnnotationClick(annotationId, isCommentsPanelShown);
							});
						}
					},
				},
			],
		};
	}, [
		analyticsSource,
		createAnalyticsEvent,
		flags,
		getCurrentCommentsPanelState,
		intl,
		isCommentsPanelShown,
		isViewPage,
	]);
}
